import { Flex, Typography } from '@iheartjane/reefer';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';

import type {
  AlgoliaProduct,
  AlgoliaReviews,
  CurrentSort,
} from '@jane/search/types';
import { makeCurrentSort, reviewsSortOptions } from '@jane/search/types';
import { Avatar } from '@jane/shared/components';
import type { MaterializedMenuProduct } from '@jane/shared/types';

import { useKioskSpacing } from '../../../hooks';
import { ProductDetailsSection } from '../ProductDetailsSection/ProductDetailsSection';
import { SortButton } from '../SortButton';
import { StarRating } from '../StarRating/StarRating';

type BaseProductReviewsSectionProps = {
  currentSort: CurrentSort;
  isLoading: boolean;
  product: AlgoliaProduct | MaterializedMenuProduct;
  productReviews: AlgoliaReviews;
  setCurrentSort: Dispatch<SetStateAction<CurrentSort>>;
};

export type ProductReviewsSectionProps =
  PropsWithChildren<BaseProductReviewsSectionProps>;

export const ProductReviewsSection = ({
  children,
  product,
  productReviews,
  isLoading,
  currentSort,
  setCurrentSort,
}: ProductReviewsSectionProps) => {
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  return (
    <ProductDetailsSection
      ariaLabel={'verified review section'}
      isLoading={isLoading}
      title={'Verified reviews'}
    >
      <Flex position="relative" flexDirection="column">
        <Flex justifyContent="space-between" alignItems="center">
          <StarRating
            aggregateRating={product.aggregate_rating}
            reviewCount={product.review_count ?? undefined}
          />
          <SortButton
            aria-label="product review sort"
            currentSort={currentSort}
            filled={false}
            condensed
            onSort={(newSort) => setCurrentSort(makeCurrentSort(newSort))}
            options={reviewsSortOptions}
          />
        </Flex>
        {productReviews.map(({ comment, nickname, rating, avatar }, idx) => (
          <Flex
            flexDirection="column"
            key={`${comment} ${idx}`}
            mt={getKioskSpacing(24, 16)}
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Flex alignItems="center" mb={getKioskSpacing(12, 8)}>
                <Avatar avatar={avatar} size="lg" />
                <Typography ml={12} variant="mini-bold">
                  {nickname}
                </Typography>
              </Flex>
              <StarRating aggregateRating={rating} isCondensed />
            </Flex>
            <Flex>
              <Typography variant={isIpad ? 'body' : 'mini'}>
                {comment}
              </Typography>
            </Flex>
          </Flex>
        ))}
        {children}
      </Flex>
    </ProductDetailsSection>
  );
};
